import * as React from "react";

import type { ToastActionElement, ToastProps } from "@/components/ui/toast";
import { useToastContext } from "@/components/ui/toasts/hooks";

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

const genId = () => (Math.random() * 999999).toString();

type Toast = Omit<ToasterToast, "id">;

function useToast() {
  const { state, setState } = useToastContext();

  const toast = React.useCallback(
    ({ ...props }: Toast) => {
      const id = genId();

      const update = (props: ToasterToast) =>
        setState(state => state.map(toast => (toast.id === id ? { ...toast, ...props } : toast)));

      const dismiss = () =>
        setState(state => state.map(toast => (toast.id === id ? { ...toast, open: false } : toast)));

      setState(prev =>
        prev.concat({
          duration: 2000,
          open: true,
          ...props,
          id,
        }),
      );

      return {
        id: id,
        dismiss,
        update,
      };
    },
    [setState],
  );

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) =>
      toastId
        ? setState(state => state.map(toast => (toast.id === toastId ? { ...toast, open: false } : toast)))
        : setState([]),
  };
}

export { useToast };
